/* eslint-disable react/jsx-max-depth */
import { useCallback, useState } from 'react';
import {
  Navigate,
  Outlet,
  ScrollRestoration,
  useLocation,
} from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { JSX } from 'react';

import Box from '@mui/material/Box';

import { getDocumentName, graphql } from '@e-socna/data-layer';
import { EMPTY_OBJECT } from '@e-socna/empty';

import { useAuthenticationContext } from '../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';
import { PATH_LOGIN } from '../../app/constants/paths.constant';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { useInterval } from '../../hooks/useInterval.hook';
import { ReferencesContextProvider } from '../../contexts/ReferencesContext';
import { NotificationsContextProvider } from '../../contexts/NotificationsContext';
import { PrinterContextProvider } from '../../contexts/PrinterContext';
import { LinearProgress } from '../../components/LinearProgress';
import { Loading } from '../../components/Loading';
import { SyncUiSettings } from '../../components/SyncUiSettings';
import { CheckGeolocation } from '../../components/CheckGeolocation';
import { AuthenticationContextProvider_AuthenticatedUser_QueryDocument } from '../../contexts/AuthenticationContext';
import { useGraphQLMutation } from '../../hooks/useGraphQLMutation.hook';
import { FileUploaderContextProvider } from '../../contexts/FileUploaderContext';
import { NavigationContextProvider } from '../../contexts/NavigationContext';
import { UppyUploadContextProvider } from '../../contexts/UppyUploadContext';
import { SentryWrapper } from '../../components/SentryWrapper';
import { PasswordChangePage } from '../../pages/PasswordChange';

import { AppBar } from '../components/AppBar';
import { MainContent } from '../components/MainContent';

import { AppMenu } from './AppMenu';

export const LoggedInLayout_RefreshToken_Mutation = graphql(`
  mutation LoggedInLayout_RefreshToken {
    refreshToken {
      _id
    }
  }
`);

interface Props {
  readonly isInPrintMode?: boolean;
}

export const LoggedInLayout = ({
  isInPrintMode = false,
}: Readonly<Props>): JSX.Element => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const queryClient = useQueryClient();
  const location = useLocation();
  const { authenticationQuery, authenticationData } =
    useAuthenticationContext();
  const { ui } = useUiContext();

  const { mutate: refreshTokenMutation } = useGraphQLMutation(
    LoggedInLayout_RefreshToken_Mutation,
  );

  const letsRefreshIt = () => {
    refreshTokenMutation(EMPTY_OBJECT, {
      onError: () => {
        // logout user if any problem
        queryClient.setQueryData(
          [
            getDocumentName(
              AuthenticationContextProvider_AuthenticatedUser_QueryDocument,
            ),
          ],
          { mustLogout: true },
        );
      },
    });
  };

  useInterval(letsRefreshIt, 300000); // 5 minutes

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prevValue) => !prevValue);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setMobileOpen(false);
  }, []);

  if (
    authenticationQuery.status === 'error' ||
    (authenticationQuery.data && 'mustLogout' in authenticationQuery.data)
  ) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate replace state={{ from: location }} to={PATH_LOGIN} />;
  }

  const isLoading = authenticationQuery.status === 'pending';

  // Nothing else is allowed by the back until the password is changed.
  if (!isLoading && authenticationData?.mustChangePassword) {
    return (
      <SentryWrapper>
        <AppBar variant="loggedOff" />
        <MainContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
          variant="loggedOff"
        >
          <PasswordChangePage />
        </MainContent>
      </SentryWrapper>
    );
  }

  if (isInPrintMode) {
    if (isLoading) {
      return <Loading />;
    }
    return (
      <ReferencesContextProvider>
        <ScrollRestoration />
        <Outlet />
      </ReferencesContextProvider>
    );
  }

  return (
    <SentryWrapper>
      <UppyUploadContextProvider>
        <ScrollRestoration />
        <Box sx={{ display: 'flex' }}>
          <ReactQueryDevtools
            buttonPosition="bottom-left"
            initialIsOpen={false}
          />
          <NotificationsContextProvider>
            <CheckGeolocation />
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
            {!!ui.isBackgroundOperationRunning && <LinearProgress />}
            <AppBar onDrawerToggle={handleDrawerToggle} variant="loggedIn" />
            {isLoading ? (
              <Loading />
            ) : (
              <ReferencesContextProvider>
                <NavigationContextProvider>
                  <PrinterContextProvider>
                    <FileUploaderContextProvider>
                      <AppMenu
                        mobileOpen={mobileOpen}
                        onDrawerClose={handleDrawerClose}
                      />
                      <MainContent variant="loggedIn">
                        <SyncUiSettings />
                        <Outlet />
                      </MainContent>
                    </FileUploaderContextProvider>
                  </PrinterContextProvider>
                </NavigationContextProvider>
              </ReferencesContextProvider>
            )}
          </NotificationsContextProvider>
        </Box>
      </UppyUploadContextProvider>
    </SentryWrapper>
  );
};
